import { Link } from "@tanstack/react-router";
import { Truck } from "lucide-react";
import { StatusPill } from "@/components/status-pill";
import { ShipmentDateBadges } from "@/components/shipment-dates";
import { sentenceCase, shipmentTone } from "@/lib/status-tones";
import { shipmentReference } from "@/lib/shipments";
import { formatDateOnly } from "@/lib/utils";

export type POShipmentLineShipment = {
  id: number;
  uuid: string;
  status: string;
  carrier_name?: string | null;
  tracking_numbers?: string | null;
  pro_number?: string | null;
  estimated_ship_date?: string | null;
  estimated_delivery_date?: string | null;
  actual_ship_date?: string | null;
  pickup_date?: string | null;
};

/** One shipment of a purchase order: status, carrier, tracking/PRO and ship dates. */
export function POShipmentLine({
  shipment,
  linkReference = false,
  aligned = false,
}: {
  shipment: POShipmentLineShipment;
  /** Link the tracking/PRO to the shipment (only when not already inside a link). */
  linkReference?: boolean;
  /** A row of the parent's two-column grid: the pill in a shared column, the rest beside it. */
  aligned?: boolean;
}) {
  const reference = shipmentReference(shipment);
  const pill = (
    <StatusPill tone={shipmentTone(shipment.status)}>
      {sentenceCase(shipment.status ?? "pending")}
    </StatusPill>
  );
  const truck = <Truck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />;
  const details = (
    <>
      {shipment.carrier_name && (
        <span className="min-w-0 truncate">
          {/* Some carriers are stored with the name doubled ("UPS UPS Ground"). */}
          {shipment.carrier_name.replace(/^(\S+)\s+\1\b/i, "$1")}
        </span>
      )}
      {reference &&
        (linkReference ? (
          <Link
            to="/shipments/$shipmentId"
            params={{ shipmentId: shipment.uuid }}
            className="font-mono text-[11px] text-primary hover:underline"
          >
            {reference}
          </Link>
        ) : (
          <span className="font-mono text-[11px] text-muted-foreground">{reference}</span>
        ))}
      <ShipmentDateBadges shipment={shipment} />
      {shipment.pickup_date && !shipment.actual_ship_date && !shipment.estimated_ship_date ? (
        <span className="text-muted-foreground">Pickup {formatDateOnly(shipment.pickup_date)}</span>
      ) : null}
    </>
  );
  if (aligned) {
    return (
      <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:col-span-2 sm:grid sm:grid-cols-subgrid sm:justify-items-start">
        {pill}
        <span className="flex min-w-0 sm:w-full flex-wrap items-center gap-x-2.5 gap-y-1">
          {truck}
          {details}
        </span>
      </span>
    );
  }
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
      {truck}
      {pill}
      {details}
    </span>
  );
}
